import { CallEvent } from '@modules/SoftPhoneModule/types';
import { InboxContext } from '../types';
import { setOutboundCallToOffered } from '../callHandlers/outboundCalls';
import { setCallToOfferedWithSoftphoneCall } from '../callHandlers/callStateHandler';
import TasksModule from '@modules/TasksModule';
import DiagnosticsModule, {
  MetricNames,
  MetricTags,
  OperationNames,
} from '@modules/DiagnosticsModule';
async function handleCallPlaced(context: InboxContext, event: CallEvent) {
  const taskId = event.pcsConversationSid || event.callId;
  const isOutboundQuickCall = event.remoteCallId.includes('quickcall');

  if (event.originating || isOutboundQuickCall) {
    let targetNumber = event.remoteIdentity; // number to which the call is made: for softphone calls, the target number is the remoteIdentity

    if (isOutboundQuickCall) {
      // if the call is made via quick call API, we need to extract the target number from the remoteIdentity without the +1
      // if the call made by providing a targetName, the remoteIdentity will be something like "<name> (+12891231234)"
      // if the call is made without a targetName, the remoteIdentity will be something like "Device QuickCall (+12891231234)"
      const phoneNumberRegex = /\+1(\d{10})/;
      const phoneNumberMatch = phoneNumberRegex.exec(event.remoteIdentity);
      if (phoneNumberMatch) {
        targetNumber = phoneNumberMatch[1];
      } else {
        context.logger.error(
          `QuickCallAPI call with remoteCallId ${event.remoteCallId} does not have a valid phone number in remoteIdentity: ${event.remoteIdentity}`,
          event,
        );
      }
    }

    context.logger.debug(
      `Outgoing call ${isOutboundQuickCall ? 'via QuickCallAPI' : ''} to (${targetNumber})`,
      event,
    );
    setOutboundCallToOffered(
      context,
      taskId,
      targetNumber,
      {
        callId: event.callId,
      },
      event.remoteCallId,
    );
  } else if (event.terminating) {
    /*
     * check for the task first: it's possible that it's created and offered by a session offered event (connecting)
     * if not, set the call to offered (which should handle the task creation and task state)
     * if there is a task we need to make sure that it's not "isConnecting" anymore as we received the softphone call
     */

    // since we don't have the pcsConversationSid in the call event, we need to check the task by remoteIdentity or localIdentity to match session's source.address
    const task = TasksModule.getConnectingCallTaskByRemoteOrLocalIdentity(
      event.remoteIdentity,
      event.localIdentity,
    );

    if (!task) {
      setCallToOfferedWithSoftphoneCall(context, {
        callId: taskId,
        remoteIdentity: event.remoteIdentity,
        localIdentity: event.localIdentity,
        remoteCallId: event.remoteCallId,
      });
    } else {
      // Task exists - emit latency metric
      const latencyMs = Date.now() - task.startedAt.getTime();
      context.logger.info(
        `[Latency Metric] handleCallPlaced - latency metric`,
        {
          taskId: task.id,
          latencyMs,
        },
      );
      DiagnosticsModule.recordHistogram(
        MetricNames.LATENCY_DURATION,
        latencyMs,
        { [MetricTags.OPERATION_NAME]: OperationNames.CALL_PLACED },
      );

      // make sure that the task is not in connected state anymore
      TasksModule.setTaskAttribute(task.id, 'isConnecting', false); // the task shouldn't be in connecting state as we receive the softphone call
      TasksModule.setTaskAttributes(task.id, {
        ...task.attributes,
        callId: event.callId,
        remoteIdentity: event.remoteIdentity,
        localIdentity: event.localIdentity,
        remoteCallId: event.remoteCallId,
      });
    }
  }
}

export default handleCallPlaced;
